// ---------------------------------------------------------------------------
// Homepage.
//
// Sections render in the order the database gives them. Product features sit
// between the catalog and the remaining sections, exactly as in the original
// markup, and stay hidden (`.product-feature` is display:none) until a catalog
// card selects one.
// ---------------------------------------------------------------------------

import { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/apiClient.js';
import { useSite } from '../context/SiteProvider.jsx';
import { BLOCK_COMPONENTS, CatalogBlock } from '../components/site/blocks.jsx';
import { CatalogCard } from '../components/product/CatalogCard.jsx';
import { ProductFeature } from '../components/product/ProductFeature.jsx';
import { CatalogGridSkeleton } from '../components/common/Skeletons.jsx';

export default function Home() {
  const { blocks, pick } = useSite();
  const location = useLocation();
  const [activeDomId, setActiveDomId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['products', 'detailed'],
    queryFn: () => api.get('/products?detailed=true'),
    staleTime: 5 * 60 * 1000,
  });

  const products = data?.products ?? [];

  const selectProduct = useCallback((product) => {
    setActiveDomId(product.domId);
    window.history.replaceState(null, '', `#${product.domId}`);
    requestAnimationFrame(() => {
      document.getElementById(product.domId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Deep links like /#majestic-product reveal that product without scrolling
  // past it — same as the original page load behaviour.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash || !products.length) return;
    const match = products.find((product) => product.domId === hash);
    if (match) setActiveDomId(match.domId);
  }, [location.hash, products]);

  // Nav links (#home, #about, #treatments, #catalog, #wholesale, #contact) are
  // plain section anchors — scroll to them on click. Product deep links are
  // excluded since those are handled above and deliberately don't scroll.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;
    if (products.some((product) => product.domId === hash)) return;
    const el = document.getElementById(hash);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [location.hash, products]);

  // The dividers that separate category groups in the original markup.
  const renderProducts = () => {
    let lastCategory = null;
    return products.map((product) => {
      const categorySlug = product.category?.slug ?? null;
      const showDivider = categorySlug && categorySlug !== lastCategory;
      lastCategory = categorySlug;

      return (
        <Fragment key={product.id}>
          {showDivider ? (
            <div className="product-category-divider" aria-hidden="true">
              <span>{pick(product.category.divider) || pick(product.category.name)}</span>
            </div>
          ) : null}
          <ProductFeature product={product} isActive={product.domId === activeDomId} />
        </Fragment>
      );
    });
  };

  return (
    <main id="main">
      {blocks.map((block) => {
        if (block.key === 'catalog') {
          return (
            <Fragment key={block.key}>
              <CatalogBlock block={block}>
                {isLoading
                  ? <CatalogGridSkeleton count={8} />
                  : products.map((product) => (
                      <CatalogCard key={product.id} product={product} onSelect={selectProduct} />
                    ))}
              </CatalogBlock>
              {renderProducts()}
            </Fragment>
          );
        }

        const Component = BLOCK_COMPONENTS[block.key];
        return Component ? <Component key={block.key} block={block} /> : null;
      })}
    </main>
  );
}
